/**
 * V6 Browse + filter panel — live client-side filtering.
 *
 * Wraps the v6 home's search input + filter chip row + featured
 * grid in a single client component so typing in the search field
 * filters the visible markets in real time, and the chips
 * (Trending / Volume / New / Ending / Hide Crypto / Hide Sports)
 * actually re-sort or hide markets. Prior to 2026-05-24 these were
 * decorative: the search posted to a non-existent /search route on
 * submit and the chips had no onClick. Founder flagged both 2026-05-24.
 *
 * The full markets list is fetched server-side and passed in as
 * `initialMarkets`; filtering happens entirely in memory, so there's
 * no per-keystroke network call.
 *
 * The grid uses V6MarketCard. Empty-state copy appears when the
 * combined filters knock the list to zero.
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Search as SearchIcon,
  Flame,
  BarChart3,
  Sparkles,
  Clock,
  LayoutGrid,
  X as XIcon,
  type LucideIcon,
} from "lucide-react";

// Human label for the active-category pill. Mirrors the labels shown
// in V6LeftCategorySidebar / V6MobileCategoryRail so a sidebar-click
// reads back the same word in the active-filter chip.
const CATEGORY_LABEL: Record<string, string> = {
  sports: "Sports",
  politics: "Politics",
  crypto: "Crypto",
  economics: "Finance",
  world: "Geopolitics",
  culture: "Culture",
  tech: "Tech",
  ai: "AI",
};
function categoryLabelOf(slug: string): string {
  return CATEGORY_LABEL[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1);
}
import { api, type MarketListItem } from "@/lib/api";
import { V6MarketCard } from "@/components/v6/MarketCard";
import { V6MobileCategoryRail } from "@/components/v6/MobileCategoryRail";

type Sort = "all" | "trending" | "volume" | "new" | "ending";

export function V6BrowseFilterPanel({
  initialMarkets,
  showCategoryRailOnDesktop = false,
}: {
  initialMarkets: MarketListItem[];
  /**
   * 2026-05-25 — on /markets the founder wants the full category set
   * rendered as a row UNDER the sort/toggle chips on desktop, not
   * just on mobile. Landing page (/) keeps the rail mobile-only.
   */
  showCategoryRailOnDesktop?: boolean;
}) {
  // 2026-05-24 — URL params drive initial filter state so chips
  // from the mobile search sheet (and any deep-link) land with the
  // right filter pre-selected. Server-side default sort param is
  // mapped onto the panel's smaller Sort union ("trending",
  // "volume", "opens_desc"→"new", "closes_asc"→"ending", …).
  const searchParams = useSearchParams();
  const initialSort = parseSortParam(searchParams?.get("sort") ?? null);
  const initialCategory = searchParams?.get("category") ?? null;

  const [query, setQuery] = useState("");
  // Founder direction 2026-05-24: this grid is the All-markets surface,
  // NOT a trending duplicate of the hero. Defaults to "all" (server
  // order preserved). Trending becomes one chip option among the
  // sort set.
  const [sort, setSort] = useState<Sort>(initialSort);
  const [hideCrypto, setHideCrypto] = useState(false);
  const [hideSports, setHideSports] = useState(false);
  // Bet365-style category tile rail (V6MobileCategoryRail). Null =
  // "All". When set, filters the grid to that categorySlug entirely
  // client-side — no network round-trip, no page nav.
  const [category, setCategory] = useState<string | null>(initialCategory);
  // 2026-05-25 — sync local category state when URL changes (e.g.
  // sidebar nav to a different category). Without this the chip
  // sticks on the first-mount value and clicking Sports → Crypto
  // in the sidebar leaves the pill showing "Sports".
  const urlCategory = searchParams?.get("category") ?? null;
  useEffect(() => {
    setCategory(urlCategory);
  }, [urlCategory]);
  // 2026-05-24 — autosuggest dropdown state. `focused` controls
  // visibility; mousedown on a row navigates before blur fires so
  // the dropdown doesn't dismiss before the click registers.
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router = useRouter();
  // 2026-05-24 — server-side search results. The page only fetches
  // ~50 markets for the in-memory grid; full-catalog search hits
  // /v1/markets?q= via api.listMarkets so typing "lula" finds
  // markets even when they're outside the loaded slice.
  // 80ms debounce — below perceived latency, but still consolidates
  // a burst of keystrokes into a single request. Founder direction
  // 2026-05-24: "just lul..." should pre-filter fast.
  const [serverHits, setServerHits] = useState<MarketListItem[]>([]);
  const [serverLoading, setServerLoading] = useState(false);
  useEffect(() => {
    const needle = query.trim();
    if (needle.length === 0) {
      setServerHits([]);
      setServerLoading(false);
      return;
    }
    setServerLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const res = await api.listMarkets({ q: needle, limit: 8 });
        setServerHits(res.markets);
      } catch {
        setServerHits([]);
      } finally {
        setServerLoading(false);
      }
    }, 80);
    return () => window.clearTimeout(handle);
  }, [query]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    // Start with the loaded slice. When a search is active, also
    // fold in server hits so markets outside initialMarkets (only
    // 50 fetched) still surface in the grid — founder caught
    // 2026-05-24 that "lula" showed in the dropdown but the grid
    // said "No matches".
    let rows: MarketListItem[] = initialMarkets;
    if (needle.length > 0 && serverHits.length > 0) {
      const seen = new Set(initialMarkets.map((m) => m.id));
      const extras = serverHits.filter((m) => !seen.has(m.id));
      rows = extras.length > 0 ? [...initialMarkets, ...extras] : initialMarkets;
    }

    if (needle.length > 0) {
      rows = rows.filter((m) => {
        const titleEn = (m.title?.en ?? "").toLowerCase();
        const titlePt = (m.title?.pt ?? "").toLowerCase();
        const slug = m.slug.toLowerCase();
        return (
          titleEn.includes(needle) ||
          titlePt.includes(needle) ||
          slug.includes(needle)
        );
      });
    }

    if (category) rows = rows.filter((m) => m.categorySlug === category);
    if (hideCrypto) rows = rows.filter((m) => m.categorySlug !== "crypto");
    if (hideSports) rows = rows.filter((m) => m.categorySlug !== "sports");

    const sorted = [...rows];
    switch (sort) {
      case "volume":
        sorted.sort((a, b) => {
          const av = BigInt(a.totalVolumeCents || "0");
          const bv = BigInt(b.totalVolumeCents || "0");
          if (av === bv) return 0;
          return av < bv ? 1 : -1;
        });
        break;
      case "new":
        sorted.sort((a, b) => Date.parse(b.opensAt) - Date.parse(a.opensAt));
        break;
      case "ending":
        sorted.sort((a, b) => Date.parse(a.closesAt) - Date.parse(b.closesAt));
        break;
      case "trending":
        // server-side trending order arrives via initialMarkets;
        // surface that by re-sorting on `lastTradeAt` desc as a rough
        // proxy when the consumer already pre-sorted by something else.
        sorted.sort((a, b) => {
          const at = a.lastTradeAt ? Date.parse(a.lastTradeAt) : 0;
          const bt = b.lastTradeAt ? Date.parse(b.lastTradeAt) : 0;
          return bt - at;
        });
        break;
      case "all":
      default:
        // preserve incoming order (server default — typically by
        // created_at desc or whatever the API picks)
        break;
    }
    return sorted;
  }, [initialMarkets, query, sort, hideCrypto, hideSports, category, serverHits]);

  // Top suggestions for the autosuggest dropdown. Merges the
  // already-loaded `initialMarkets` (instant feedback as the user
  // types) with `serverHits` (full catalog via /v1/markets?q=) so
  // results show up the moment the local set has them and grow as
  // the server responds. De-dupes by market id, local-first.
  const suggestions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return [];
    const localHits = initialMarkets.filter((m) => {
      const titleEn = (m.title?.en ?? "").toLowerCase();
      const titlePt = (m.title?.pt ?? "").toLowerCase();
      const slug = m.slug.toLowerCase();
      return (
        titleEn.includes(needle) ||
        titlePt.includes(needle) ||
        slug.includes(needle)
      );
    });
    const seen = new Set(localHits.map((m) => m.id));
    const merged = [...localHits];
    for (const m of serverHits) {
      if (seen.has(m.id)) continue;
      merged.push(m);
      seen.add(m.id);
    }
    return merged.slice(0, 8);
  }, [initialMarkets, query, serverHits]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const first = suggestions[0];
    if (first) {
      router.push(`/markets/${first.slug}`);
      setFocused(false);
      inputRef.current?.blur();
    }
  }

  // Always show dropdown while the input is focused and the query
  // has content — even when results are empty, we want a "No
  // matches" hint visible so the user gets immediate feedback.
  const showDropdown = focused && query.trim().length > 0;

  return (
    <>
      {/* Live search */}
      <section className="mx-auto mt-5 w-full max-w-[1280px] px-4">
        <form
          onSubmit={handleSubmit}
          role="search"
          className="relative flex w-full items-center gap-2"
        >
          <label
            className="flex h-11 flex-1 items-center gap-2 rounded-lg border px-3.5 sm:h-10"
            style={{
              background: "var(--color-surface)",
              borderColor: "var(--color-border)",
            }}
          >
            <SearchIcon
              size={16}
              strokeWidth={1.75}
              aria-hidden="true"
              style={{ color: "var(--color-text3)" }}
            />
            <input
              ref={inputRef}
              type="search"
              name="q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => {
                // Delay so a mousedown on a suggestion row fires before
                // the dropdown unmounts.
                window.setTimeout(() => setFocused(false), 120);
              }}
              placeholder="Search markets…"
              aria-label="Search markets"
              aria-autocomplete="list"
              aria-expanded={showDropdown}
              autoComplete="off"
              className="w-full min-w-0 border-0 bg-transparent text-[14px] outline-none placeholder:opacity-70"
              style={{ color: "var(--color-text)" }}
            />
            {query.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="rounded p-0.5 text-[12px] leading-none"
                style={{ color: "var(--color-text3)" }}
              >
                ✕
              </button>
            )}
          </label>

          {/* Autosuggest dropdown — local hits stream in instantly,
              server hits merge in after the 200ms debounce. Shows
              even on zero matches so the user gets feedback. */}
          {showDropdown && (
            <div
              role="listbox"
              className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border shadow-lg"
              style={{
                background: "var(--color-surface)",
                borderColor: "var(--color-border)",
              }}
            >
              {suggestions.length === 0 ? (
                serverLoading ? (
                  // While the first request is in flight and we have
                  // no local hits, render nothing — the dropdown will
                  // fill in as soon as the server replies (~tens of
                  // ms). Showing a "Searching…" placeholder felt
                  // sluggish per founder direction 2026-05-24.
                  <div className="px-3 py-2 text-[11px]" style={{ color: "var(--color-text3)" }}>
                    …
                  </div>
                ) : (
                  <div
                    className="px-3 py-4 text-center text-[12px]"
                    style={{ color: "var(--color-text2)" }}
                  >
                    <div className="font-medium">
                      No markets match "{query.trim()}"
                    </div>
                    <div
                      className="mt-1 text-[11px]"
                      style={{ color: "var(--color-text3)" }}
                    >
                      Try a different keyword or browse a category
                      below.
                    </div>
                  </div>
                )
              ) : (
                <>
                  {suggestions.map((m, idx) => {
                    const title = m.title?.en ?? m.slug;
                    const yes = m.yesPriceCents ?? 50;
                    return (
                      <Link
                        key={m.id}
                        href={`/markets/${m.slug}`}
                        role="option"
                        aria-selected={idx === 0}
                        onMouseDown={() => setFocused(false)}
                        className="flex items-center justify-between gap-3 border-t px-3 py-2.5 no-underline first:border-t-0 hover:bg-[var(--color-surface2)]"
                        style={{
                          borderColor: "var(--color-border)",
                          color: "var(--color-text)",
                        }}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-1 text-[13px] font-medium">
                            {title}
                          </div>
                          <div
                            className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.08em]"
                            style={{ color: "var(--color-text3)" }}
                          >
                            {m.categorySlug ?? "market"}
                          </div>
                        </div>
                        <span
                          className="flex-shrink-0 font-mono text-[12px] font-semibold tabular-nums"
                          style={{ color: "var(--color-text2)" }}
                        >
                          {yes}¢
                        </span>
                      </Link>
                    );
                  })}
                  <div
                    className="flex items-center justify-between border-t px-3 py-1.5 text-[10px]"
                    style={{
                      borderColor: "var(--color-border)",
                      color: "var(--color-text3)",
                      background: "var(--color-bg)",
                    }}
                  >
                    <span>↵ Enter to open the first match</span>
                    {serverLoading && <span>Searching…</span>}
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </section>

      {/* Filter chips */}
      <section
        className="mx-auto mt-4 flex w-full max-w-[1280px] items-center gap-2 overflow-x-auto px-4"
        style={{ scrollbarWidth: "none" }}
      >
        {/* 2026-05-25 — when a category is active (set via the sidebar
            or the mobile rail), surface it as a prominent removable
            ochre pill at the start of the row so the user sees what's
            filtered + has a one-tap reset. Clicking the × clears
            both the local state AND the URL so a refresh doesn't
            re-apply the filter. */}
        {category && (
          <button
            type="button"
            onClick={() => {
              setCategory(null);
              router.replace("/markets");
            }}
            aria-label={`Clear ${categoryLabelOf(category)} filter`}
            className="hidden sm:inline-flex min-h-[36px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-[12px] font-semibold leading-none transition-colors"
            style={{
              background:
                "color-mix(in srgb, var(--color-accent) 16%, transparent)",
              color: "var(--color-text)",
              border:
                "1px solid color-mix(in srgb, var(--color-accent) 60%, transparent)",
            }}
          >
            <span>{categoryLabelOf(category)}</span>
            <XIcon
              size={13}
              strokeWidth={2}
              aria-hidden="true"
              style={{ color: "var(--color-accent)" }}
            />
          </button>
        )}
        <SortChip
          label="All markets"
          icon={LayoutGrid}
          // 2026-05-25 — "All markets" reads as active only when no
          // category is filtered AND no specific sort is set.
          // Clicking it also clears the category + URL so it acts as
          // a "reset everything" affordance, not just a sort change.
          active={sort === "all" && !category}
          onClick={() => {
            setSort("all");
            if (category) {
              setCategory(null);
              router.replace("/markets");
            }
          }}
        />
        <SortChip
          label="Trending"
          icon={Flame}
          active={sort === "trending"}
          onClick={() => setSort("trending")}
        />
        <SortChip
          label="Volume"
          icon={BarChart3}
          active={sort === "volume"}
          onClick={() => setSort("volume")}
        />
        <SortChip
          label="New"
          icon={Sparkles}
          active={sort === "new"}
          onClick={() => setSort("new")}
        />
        <SortChip
          label="Closing soon"
          icon={Clock}
          active={sort === "ending"}
          onClick={() => setSort("ending")}
        />
        <span
          aria-hidden="true"
          className="mx-1 h-[22px] w-px flex-shrink-0"
          style={{ background: "var(--color-border2)" }}
        />
        <ToggleChip
          label="Hide Crypto"
          active={hideCrypto}
          onClick={() => setHideCrypto((v) => !v)}
        />
        <ToggleChip
          label="Hide Sports"
          active={hideSports}
          onClick={() => setHideSports((v) => !v)}
        />
      </section>

      {/* Bet365-style category tile rail — instant in-page filter,
          no nav. Sits below the existing sort/toggle chips.
          showOnDesktop=true on /markets per 2026-05-25 founder ask. */}
      <V6MobileCategoryRail
        active={category}
        onSelect={setCategory}
        showOnDesktop={showCategoryRailOnDesktop}
      />

      {/* Filtered grid */}
      <section
        className="mt-6 border-t"
        style={{ borderColor: "var(--color-border)" }}
      >
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 sm:px-5 sm:py-10">
          <div className="mb-5 flex items-baseline justify-between gap-3">
            <h2 className="text-[18px] font-bold tracking-[-0.02em] sm:text-2xl">
              {labelForSort(sort)}
              <span
                className="ml-2 font-mono text-[11px] font-normal"
                style={{ color: "var(--color-text3)" }}
              >
                {filtered.length}
              </span>
            </h2>
          </div>
          {filtered.length === 0 ? (
            <div
              className="rounded-xl border px-5 py-10 text-center text-[14px]"
              style={{
                borderColor: "var(--color-border)",
                background: "var(--color-surface)",
                color: "var(--color-text2)",
              }}
            >
              No markets match{query ? ` "${query}"` : " those filters"}.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.slice(0, 24).map((m) => (
                <V6MarketCard
                  key={m.id}
                  market={m}
                  trending={sort === "trending"}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function SortChip({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex min-h-[36px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-[12px] leading-none transition-colors"
      style={{
        background: active
          ? "color-mix(in srgb, var(--color-accent) 12%, transparent)"
          : "transparent",
        color: active ? "var(--color-text)" : "var(--color-text2)",
        border: `1px solid ${
          active
            ? "color-mix(in srgb, var(--color-accent) 55%, transparent)"
            : "var(--color-border)"
        }`,
        fontWeight: active ? 600 : 500,
      }}
    >
      <Icon
        size={13}
        strokeWidth={1.75}
        aria-hidden="true"
        style={active ? { color: "var(--color-accent)" } : undefined}
      />
      {label}
    </button>
  );
}

function ToggleChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="inline-flex min-h-[36px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] leading-none transition-colors"
      style={{
        background: active
          ? "color-mix(in srgb, var(--color-accent) 12%, transparent)"
          : "transparent",
        color: active ? "var(--color-text)" : "var(--color-text2)",
        border: `1px solid ${
          active
            ? "color-mix(in srgb, var(--color-accent) 55%, transparent)"
            : "var(--color-border)"
        }`,
        fontWeight: active ? 600 : 500,
      }}
    >
      {label}
    </button>
  );
}

// 2026-05-24 — map the broader URL `sort` vocabulary used by the
// mobile search sheet (closes_asc / opens_desc / liquidity /
// competitive) onto the panel's Sort union. Unknown values fall
// through to "all" so deep-links never break the filter UI.
function parseSortParam(raw: string | null): Sort {
  if (!raw) return "all";
  switch (raw) {
    case "trending":
      return "trending";
    case "volume":
    case "liquidity":
    case "competitive":
      return "volume";
    case "opens_desc":
    case "new":
      return "new";
    case "closes_asc":
    case "ending":
      return "ending";
    case "all":
      return "all";
    default:
      return "all";
  }
}

function labelForSort(s: Sort): string {
  switch (s) {
    case "trending":
      return "Trending markets";
    case "volume":
      return "Top by volume";
    case "new":
      return "New markets";
    case "ending":
      return "Closing soon";
    case "all":
    default:
      return "All markets";
  }
}
